<template>
  <filter-parent
    :aria-label-id="'place-filter'"
    :title="texts.location"
    :start-open="true"
    :icon="'location-pin'"
  >
    <places-autocomplete-input
      :aria-labelledby="'place-filter'"
      :placeholder="texts.startTyping"
      :preselected-city="city || cityFromUrl"
      :preselected-coordinates="preselectedCoordinates"
      @place-selected="setFilteredPlace"
      @place-removed="setFilteredPlace"
    />

    <transition name="fade">
      <div v-if="hasPlace" class="allowed-distance-filter">
        <p v-text="withinDistanceText" />
        <range-slider
          v-model="allowedDistance"
          :min="minDistance"
          :max="maxDistance"
          :aria-labelledby="'place-filter'"
        />
      </div>
    </transition>
  </filter-parent>
</template>

<script lang="ts">
import PlacesAutocompleteInput from 'Components/PlacesAutocompleteInput.vue';
import RangeSlider from 'Components/RangeSlider.vue';
import { useCityTermLocalStorage } from 'Main/composables/useCityTermLocalStorage';
import type { TPlaceResult } from 'Main/services/types/counselor-grid-service-types';
import City from 'Modules/City';
import { useImperial } from 'Utils/distance';
import { safeRemoveItem } from 'Utils/safeLocalStorage';
import { sprintf } from 'sprintf-js';
import { defineComponent } from 'vue';
import { mapMutations, mapState } from 'vuex';
import FilterParent from '../common/FilterParent.vue';

export default defineComponent({
  components: {
    FilterParent,
    PlacesAutocompleteInput,
    RangeSlider,
  },

  setup() {
    const {
      cityTerm,
      saveCityTermToLocalStorage,
      getCityTermFromLocalStorage,
    } = useCityTermLocalStorage();
    return {
      cityTerm,
      saveCityTermToLocalStorage,
      getCityTermFromLocalStorage,
    };
  },

  props: {
    isCityPage: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cityFromUrl: '',
      minDistance: 5,
      maxDistance: 80,
      texts: window.theme_vars.counselorGridTexts,
      currentSite: window.theme_vars.currentSite,
      latestCityLookupId: 0,
    };
  },

  computed: {
    ...mapState('counselorGrid', ['filteredPlace']),

    city() {
      if (this.isCityPage && this.cityTerm?.name) {
        return this.cityTerm.name;
      }

      return '';
    },

    preselectedCoordinates() {
      if (
        this.isCityPage &&
        typeof this.cityTerm?.latitude === 'number' &&
        typeof this.cityTerm?.longitude === 'number'
      ) {
        return {
          lat: this.cityTerm.latitude,
          lng: this.cityTerm.longitude,
        };
      }

      return null;
    },

    defaultDistance() {
      return useImperial(this.currentSite) ? 15 : 20;
    },

    withinDistanceText() {
      const distanceWithUnit = useImperial(this.currentSite)
        ? `${this.allowedDistance} mi`
        : `${this.allowedDistance} km`;

      return sprintf(this.texts.withinDistance, distanceWithUnit);
    },

    hasPlace() {
      return !!(this.filteredPlace as TPlaceResult)?.location;
    },

    allowedDistance: {
      get() {
        return (
          this.$store.state.counselorGrid.filteredAllowedDistance ||
          this.defaultDistance
        );
      },
      set(value) {
        this.$store.commit('counselorGrid/setFilteredAllowedDistance', value);
      },
    },
  },

  watch: {
    filteredPlace(value: TPlaceResult) {
      if (value?.name && !value?.location) {
        this.cityFromUrl = value.name;
      }

      if (value && typeof value === 'object') {
        this.$store.commit(
          'counselorGrid/setFilteredAllowedDistance',
          value ? this.defaultDistance : 0
        );

        // Only a place the visitor picked says anything about which city they
        // are in. The input also emits the place it was handed to preselect —
        // this page's own city — and resolving that one back through Google and
        // OpenStreetMap can fail for a city we already have, which would clear
        // the very term it came from.
        if (value.location && !value.preselected) {
          this.updateCityFromFilteredPlace(value);
        }
      }
    },

    hasPlace(value: boolean) {
      this.allowedDistance = value ? this.defaultDistance : 0;
    },
  },

  methods: {
    ...mapMutations('counselorGrid', ['setFilteredPlace']),

    async updateCityFromFilteredPlace(place: TPlaceResult): Promise<void> {
      if (!place?.location) {
        return;
      }

      // Bridge the loading state across this async gap so the resorted list
      // doesn't flash before the dispatched loadCounselors starts.
      this.$store.commit('users/setIsLoading', true);
      const lookupId = ++this.latestCityLookupId;
      let dispatched = false;

      try {
        const latitude = place.location.lat();
        const longitude = place.location.lng();

        const response = await fetch(
          `${window.theme_vars.theme_api_url}/get-city-from-long-lat?lat=${latitude}&long=${longitude}`
        );

        // A newer place selection has started — let it own the state.
        if (lookupId !== this.latestCityLookupId) {
          return;
        }

        if (!response.ok) {
          return;
        }

        const data = await response.json();

        if (lookupId !== this.latestCityLookupId) {
          return;
        }

        const { cityId, cityName, citySlug, cityPhone } = data || {};

        // users/getUsers bails out if state.isLoading is already true. Vue
        // batches this false with the dispatch's setIsLoading(true) in one
        // tick, so the UI never renders the cleared state.
        this.$store.commit('users/setIsLoading', false);

        if (cityId && cityName && citySlug) {
          // City.setData persists localStorage and dispatches the storage event.
          City.setData(cityId, cityName, citySlug, cityPhone);
          dispatched = true;
          return;
        }

        // No matching Lavendla city — clear the previous scope before dispatch
        // so loadCounselors refetches unscoped.
        const siteKey = this.currentSite.toUpperCase();
        safeRemoveItem(`cityTerm-${siteKey}`);
        safeRemoveItem(`city-${siteKey}`);
        window.dispatchEvent(new CustomEvent('city-storage-updated-event'));
        dispatched = true;
      } catch (error) {
        console.error('Failed to update city from filtered place', error);
      } finally {
        // Only this lookup owns the loading flag — a newer one will manage its own.
        if (!dispatched && lookupId === this.latestCityLookupId) {
          this.$store.commit('users/setIsLoading', false);
        }
      }
    },
  },
});
</script>
